// Render is the currently reachable production API origin. Deployments with the
// api.scribe11.com custom domain should set VITE_API_URL at build time; keeping a
// reachable fallback prevents every dashboard request from failing when that DNS
// record is absent or a static build was produced without environment variables.
function resolveApiBase() {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL.replace(/\/$/, '')
  return 'https://scribe11-api.onrender.com'
}
export const API_BASE = resolveApiBase()

// Session profile keys — only non-sensitive profile data is kept in localStorage.
// The actual session tokens live in HttpOnly cookies (__Host-s11f / __Host-s11e)
// set by the server, which JavaScript cannot read. This eliminates the token-theft
// attack surface from XSS entirely.
export const FREE_PROFILE_KEY = 'scribe11.profile.free'
export const ENT_PROFILE_KEY  = 'scribe11.profile.enterprise'

export function loadFreeProfile() {
  try { return JSON.parse(localStorage.getItem(FREE_PROFILE_KEY) || 'null') } catch { return null }
}
export function loadEnterpriseProfile() {
  try { return JSON.parse(localStorage.getItem(ENT_PROFILE_KEY) || 'null') } catch { return null }
}
export function saveFreeProfile(profile) {
  if (profile) localStorage.setItem(FREE_PROFILE_KEY, JSON.stringify(profile))
  else localStorage.removeItem(FREE_PROFILE_KEY)
}
export function saveEnterpriseProfile(profile) {
  if (profile) localStorage.setItem(ENT_PROFILE_KEY, JSON.stringify(profile))
  else localStorage.removeItem(ENT_PROFILE_KEY)
}

export function migrateLegacySession() {
  // No-op if already migrated.
}

// startCheckout initiates a Stripe checkout session for the given plan tier.
// On success, redirects the browser to the Stripe-hosted checkout page.
// plan: 'STARTER' | 'BUSINESS' | 'COMPLIANCE_PRO' | 'AGENCY' | 'ENTERPRISE_COMPLIANCE'
export async function startCheckout(plan) {
  const { response } = await apiRequest('/api/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data?.error || 'Checkout failed')
  if (!data?.url) throw new Error('No checkout URL returned')
  window.location.href = data.url
}

// fetchBillingProfile returns the current tenant's billing profile.
export async function fetchBillingProfile() {
  const { response } = await apiRequest('/api/v1/billing/profile')
  if (!response.ok) return null
  return response.json()
}

export async function apiRequest(path, options = {}) {
  const requestID = options.requestID || crypto.randomUUID()
  const headers = new Headers(options.headers || {})
  headers.set('X-Request-ID', requestID)
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', headers.get('Content-Type') || 'application/json')
  }
  // X-Tenant-ID header is a non-sensitive hint; the server derives the real
  // tenant from the HttpOnly cookie. We still send it so legacy/mobile paths work.
  const freeProfile = loadFreeProfile()
  const entProfile  = loadEnterpriseProfile()
  const activeProfile = entProfile || freeProfile
  if (activeProfile?.tenantId) {
    headers.set('X-Tenant-ID', activeProfile.tenantId)
  }
  // credentials: 'include' tells the browser to send HttpOnly cookies on
  // cross-origin requests to api.scribe11.com. The server must respond with
  // Access-Control-Allow-Credentials: true for this to work.
  const response = await fetch(API_BASE + path, {
    ...options,
    credentials: 'include',
    headers,
  })
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('scribe11-session-expired', {
      detail: { status: response.status, path }
    }))
  }
  return { requestID, response }
}
